import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "@/i18n";
import { API } from "@/data";
import { Search, CheckCircle2, Circle, FlaskConical, ArrowUpRight } from "lucide-react";

const CARRIERS = [
  { name: "Hapag-Lloyd", slug: "hapag", url: "https://www.hapag-lloyd.com/en/online-business/track/track-by-booking-solution.html" },
  { name: "Maersk", slug: "maersk", url: "https://www.maersk.com/tracking/" },
  { name: "MSC", slug: "msc", url: "https://www.msc.com/en/track-a-shipment" },
  { name: "CMA-CGM", slug: "cma-cgm", url: "https://www.cma-cgm.com/eBusiness/Tracking" },
];

export const Tracking = () => {
  const { lang, t } = useLang();
  const [number, setNumber] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const track = async () => {
    if (!number.trim()) return;
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}/track/${encodeURIComponent(number.trim())}`);
      setResult(data);
    } catch {
      setError(true);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="tracking" className="py-32" data-testid="tracking-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <Overline>{t("tracking.overline")}</Overline>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("tracking.title")}</h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 bg-surface border border-white/10 p-8 lg:p-10">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && track()}
              placeholder={t("tracking.placeholder")}
              className="input-tactical font-mono flex-1"
              data-testid="tracking-input"
            />
            <button onClick={track} disabled={loading} data-testid="tracking-submit-btn"
              className="bg-ember hover:bg-emberdark disabled:opacity-50 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2">
              <Search size={16} /> {loading ? "…" : t("tracking.button")}
            </button>
          </div>
          <p className="mt-3 text-xs text-steel-600 flex items-center gap-2">
            <FlaskConical size={12} /> {t("tracking.tryHint")}
          </p>

          {error && <p className="mt-6 text-ember text-sm" data-testid="tracking-error">Numéro invalide.</p>}

          {result && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 border border-white/10 bg-abyss" data-testid="tracking-result">
              <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-white/10">
                <span className="font-mono text-lg text-ember font-semibold">{result.container}</span>
                <span className="text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-2 py-1 text-steel-400">{t("tracking.demo")}</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                {[
                  [t("tracking.vessel"), result.vessel],
                  [t("tracking.route"), `${result.origin} → ${result.destination}`],
                  [t("tracking.eta"), result.eta],
                  [t("tracking.progress"), `${result.progress}%`],
                ].map(([label, value], i) => (
                  <div key={i} className="bg-abyss p-5">
                    <p className="text-[10px] uppercase tracking-widest text-steel-500">{label}</p>
                    <p className="mt-1 font-medium text-sm">{value}</p>
                  </div>
                ))}
              </div>
              <div className="px-6 py-6">
                <p className="text-sm text-steel-300 mb-4">{result.status[lang]}</p>
                <div className="h-1 bg-white/10 overflow-hidden">
                  <motion.div className="h-full bg-ember" initial={{ width: 0 }} animate={{ width: `${result.progress}%` }} transition={{ duration: 1, ease: "easeOut" }} />
                </div>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {result.milestones.map((m, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {m.done ? <CheckCircle2 size={14} className="text-ember shrink-0" /> : <Circle size={14} className="text-steel-600 shrink-0" />}
                      <span className={m.done ? "text-white" : "text-steel-600"}>{m.label[lang]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </Reveal>

        <Reveal delay={0.2} className="mt-16">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-ember mb-6">
            {lang === "fr" ? "Suivi officiel par compagnie" : "Official carrier tracking"}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="carrier-links">
            {CARRIERS.map((c) => (
              <a key={c.name} href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`carrier-link-${c.slug}`}
                className="group bg-abyss p-6 flex items-center justify-between hover:bg-surface transition-colors duration-300">
                <span className="font-display font-semibold uppercase tracking-wide text-sm">{c.name}</span>
                <ArrowUpRight size={16} className="text-steel-600 group-hover:text-ember group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs text-steel-600">
            {lang === "fr"
              ? "Utilisez votre numéro de connaissement (B/L) ou de réservation sur le site de la compagnie."
              : "Use your Bill of Lading (B/L) or booking number on the carrier's website."}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
